import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import llmService from '../../services/llmService';
import UploadPdf from './UploadPdf';
import PdfLibrary from './PdfLibrary';
import ApiKeyModal from '../ApiKeyModal/ApiKeyModal';
import './Dashboard.css';
import Footer from '../Footer';

function Dashboard({ onViewPdf }) {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('library');
  const [userPdfs, setUserPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [hasApiKeys, setHasApiKeys] = useState(false);
  const [checkingApiKeys, setCheckingApiKeys] = useState(true);
  
  // New state for the signup delay UI
  const [isFinalizing, setIsFinalizing] = useState(false);

  // -------------------------------------------------------------
  // 1️⃣ HANDLE FRESH SIGNUP (Wait & Reload Strategy)
  // -------------------------------------------------------------
  useEffect(() => {
    const needsRefresh = sessionStorage.getItem("signup_refresh_needed");

    if (needsRefresh === "true") {
      console.log("⏳ Fresh signup detected. Waiting for backend sync...");
      setIsFinalizing(true); // Lock the UI

      // Clear the flag so we don't reload infinitely
      sessionStorage.removeItem("signup_refresh_needed");

      // Wait 2 seconds, then reload
      const timer = setTimeout(() => {
        console.log("🔄 Reloading page now...");
        window.location.reload();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  // -------------------------------------------------------------
  // 2️⃣ CHECK API KEYS
  // -------------------------------------------------------------
  useEffect(() => {
    const checkApiKeyRequirement = async () => {
      // If we are waiting to reload, don't bother checking keys yet
      if (!currentUser || isFinalizing) return;
      
      try {
        setCheckingApiKeys(true);
        const requirement = await llmService.checkApiKeyRequirement();
        setHasApiKeys(requirement.hasApiKeys);
        
        if (!requirement.hasApiKeys) {
          setShowApiKeyModal(true);
        } else {
          // 🚨 FIX: Explicitly close the modal if keys are found!
          // This prevents the modal from staying open if a previous check failed
          setShowApiKeyModal(false);
        }

      } catch (error) {
        console.error('❌ Failed to check API key requirement:', error);
        setHasApiKeys(false);
        setShowApiKeyModal(true);
      } finally {
        setCheckingApiKeys(false);
      }
    };

    checkApiKeyRequirement();
  }, [currentUser, isFinalizing]); // Added isFinalizing dependency

  // -------------------------------------------------------------
  // 3️⃣ FETCH PDFS
  // -------------------------------------------------------------
  useEffect(() => {
    const fetchUserPdfs = async () => {
      // Don't fetch if finalizing or no keys
      if (!currentUser || !hasApiKeys || isFinalizing) return;
      
      try {
        setLoading(true);
        console.log('📚 Fetching PDFs for user:', currentUser.uid);
        
        const pdfs = await dashboardService.getUserPdfs();
        console.log('✅ PDFs fetched successfully:', pdfs.length);
        setUserPdfs(pdfs);
      } catch (error) {
        console.error('❌ Failed to fetch PDFs:', error);
        setUserPdfs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserPdfs();
  }, [currentUser, refreshTrigger, hasApiKeys, isFinalizing]);

  const handlePdfUploaded = () => {
    setRefreshTrigger(prev => prev + 1);
    setActiveTab('library');
  };

  const handlePdfDeleted = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleApiKeySetupComplete = () => {
    setHasApiKeys(true);
    setShowApiKeyModal(false);
    setRefreshTrigger(prev => prev + 1);
  };

  // -------------------------------------------------------------
  // 4️⃣ RENDER LOADING SCREENS
  // -------------------------------------------------------------
  
  // A. Show "Finalizing Account" screen if we are in the 2-second wait
  if (isFinalizing) {
    return (
      <div className="dashboard-loading" style={{ 
          height: '100vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center' 
      }}>
        <div className="loading-spinner"></div>
        <h2 style={{ marginTop: '20px' }}>Finalizing account setup...</h2>
        <p>Please wait a moment while we configure your workspace.</p>
      </div>
    );
  }

  // B. Show standard loading while checking keys
  if (checkingApiKeys) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Checking your configuration...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <ApiKeyModal 
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onSetupComplete={handleApiKeySetupComplete}
      />

      <div className="dashboard-header">
        {!hasApiKeys && (
          <div className="api-key-warning">
            <div className="warning-icon">⚠️</div>
            <div className="warning-content">
              <strong>Configuration Required</strong>
              <p>You need to set up an API key to upload and analyze PDFs.</p>
              <button onClick={() => setShowApiKeyModal(true)} className="setup-api-key-button">
                🔑 Setup API Key
              </button>
            </div>
          </div>
        )}
      </div>

      {hasApiKeys ? (
        <>
          <div className="dashboard-tabs">
            <button className={`tab-button ${activeTab === 'library' ? 'active' : ''}`} onClick={() => setActiveTab('library')}>
              📚 My Library ({userPdfs.length})
            </button>
            <button className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => setActiveTab('upload')}>
              📤 Upload PDF
            </button>
            <button className="tab-button api-key-button" onClick={() => setShowApiKeyModal(true)} title="Manage API Keys">
              🔑 API Keys
            </button>
          </div>

          <div className="dashboard-content">
            {activeTab === 'upload' ? (
              <UploadPdf onUploadSuccess={handlePdfUploaded} />
            ) : (
              <PdfLibrary 
                pdfs={userPdfs}
                loading={loading}
                onPdfDelete={handlePdfDeleted}
                onViewPdf={onViewPdf}
                onRefresh={() => setRefreshTrigger(prev => prev + 1)}
              />
            )}
          </div>
        </>
      ) : (
        <div className="api-key-required-state">
          <div className="required-icon">🔑</div>
          <h2>API Key Required</h2>
          <p>To start using the PDF analysis features, you need to configure at least one AI service API key.</p>
          <button onClick={() => setShowApiKeyModal(true)} className="setup-api-key-button primary">
            Get Started - Setup API Key
          </button>
          <div className="api-key-benefits">
            <h3>Why setup an API key?</h3>
            <ul>
              <li>📄 Upload and analyze PDF documents</li>
              <li>🤖 Get AI-powered summaries and insights</li>
              <li>🔍 Ask questions about your documents</li>
              <li>💾 Save and manage your document library</li>
            </ul>
          </div>
        </div>
      )}
      
      <div className="dashboard-footer-wrapper">
        <Footer />
      </div>
    </div>
  );
}

export default Dashboard;